import React, { memo, useCallback, useMemo } from 'react'
import { useFormik } from 'formik'
import clsx from 'clsx'
import { connect } from 'react-redux'
import { history } from 'store'
import { format } from 'date-fns'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import { camelCase } from 'lodash'

/* components */
import SelectComponent from 'components/ui/Select/Base'
import Button from 'components/ui/Button/base'
import DropdownCount from 'components/ui/Dropdown/Count'
import DatePicker from 'components/common/DatePicker'
import LocationSearch from 'components/common/LocationSearch'
import Icon from 'components/common/Icon'
import LocationService from 'services/LocationService'

/* selectors */
import { getCategories } from 'modules/categories/store/selectors'

/* configs */
import { options } from './config'

/* style */
import classes from './MissionSearch.module.scss'


const MissionSearch = ({ categories }) => {

  const { t } = useTranslation('home_volunteering')
  const { t: t2 } = useTranslation('missionSearch_volunteering')
  const memoT = useCallback((val) => t(val), [t])


  const themes = categories ? categories.map((value) => (
    {
      value: value.name,
      label: t2(camelCase(value.slug)),
      slug: value.slug,
    }
  )) : []
  // console.log('categories',categories)
  const date = useMemo(() => (new Date()), [])

  const form = useFormik({
    initialValues: {
      countAdults: 1,
      countChildren: 0,
      start_date: null,
      end_date: null,
      location: null,
      theme: null,
      budget: null,
    },
    onSubmit: (values) => {
      const params = {}
      const [from, to] = values.budget?.split('-') || [null, null]
      values.budget_from = from
      values.budget_to = to
      values.start_date = values.start_date && format(values.start_date, 'yyyy-MM-dd')
      values.end_date = values.end_date && format(values.end_date, 'yyyy-MM-dd')
      for (const key in values) {
        if (values[key]) {
          params[key] = values[key]
        }
      }
      delete params.location
      window.sessionStorage.setItem('location', JSON.stringify(values.location))
      history.push({
        pathname: '/search-mission',
        // eslint-disable-next-line
        search: new URLSearchParams(params).toString(),
      })
    },

  })
  const onLocationChange = useCallback((value) => {
    form.setFieldValue('location', value)
    if (value.display_name !== 'None') {
      LocationService.add(value)
        .then((location = {}) => {
          form.setFieldValue('country_id', location.country.id)
          form.setFieldValue('city_id', location.city ? location.city.id : null)
        })
        .catch(() => {
          toast.error('Adding location error')
        })
    } else {
      form.setFieldValue('country_id', null)
      form.setFieldValue('city_id', null)
    }
  }, [form])

  const handleChange = useCallback((val, name) => { form.setFieldValue(name, val.value) }, [form])

  return (
    <div className={classes.wrapperMissionSearch}>
      <form
        onSubmit={form.handleSubmit}
        className={classes.formStyle}>
        <div className={`d-flex justify-content-between ${classes.mediaColumn}`}>
          <div className={classes.blockWithSelect}>
            <SelectComponent
              label="themeSelectLabel"
              options={themes}
              name="theme"
              selected={form.values.theme}
              className={classes.flexStart}
              onChange={handleChange}
              placeholder="themeSelectPlaceholder"
            />
          </div>
          <div className={classes.blockWithInput}>
            <LocationSearch
              placeholder="locationSearchPlaceHolder"
              name="location"
              label="locationSearchLabel"
              location={form.values.location}
              onChange={onLocationChange}
              labelColor={classes.locationLabel}
              isNotRequired
            />
          </div>
        </div>
        <div className={`d-flex justify-content-between ${classes.mediaColumn}`}>
          <div className={`d-flex ${classes.mediaColumnWithDouble}`}>
            <div className={clsx(classes.blockWithInput, classes.small)}>
              <DatePicker
                label="arrivalLabel"
                name="arrivalDate"
                onChange={(val) => { form.setFieldValue('start_date', val) }}
                fieldName="start_date"
                selected={form.values.start_date}
                dateFormat="MMM do, yyyy"
                labelColor="white"
                placeholderText="datePickerPlaceholder"
                inputClassName={classes.dataPickerInput}
                style={{ marginBottom: '0' }}
                autoComplete="off"
                minDate={date}
                popperModifiers={{
                  preventOverflow: {
                    enabled: true,
                  },
                }}
              />
              <div className={classes.iconWrapper}>
                <Icon iconName="icon_help" extension="svg" />
                {/* eslint-disable-next-line react/no-unescaped-entities */}
                <div className={classes.tooltip}>
                  {memoT('arrivalDateInfo')}
                </div>
              </div>
            </div>
            <div className={clsx(classes.blockWithInput, classes.small)}>
              <DatePicker
                label="departureLabel"
                name="departureDate"
                onChange={(val) => { form.setFieldValue('end_date', val) }}
                fieldName="end_date"
                selected={form.values.end_date}
                dateFormat="MMM do, yyyy"
                labelColor="white"
                placeholderText="datePickerPlaceholder"
                inputClassName={classes.dataPickerInput}
                style={{ marginBottom: '0' }}
                autoComplete="off"
                minDate={date}
                popperModifiers={{
                  preventOverflow: {
                    enabled: true,
                  },
                }}
              />
            </div>
          </div>
          <div className={classes.blockWithSelect}>
            <DropdownCount
              label="numberOfVolunteersLabel"
              onChange={form.handleChange}
              values={form.values}
            />
          </div>
        </div>
        <div className={`d-flex justify-content-between ${classes.mediaColumnWithBtn}`}>
          <div className={classes.blockWithSelect}>
            <SelectComponent
              label="missionSearchBudgetSelectLabel"
              name="budget"
              options={options}
              className={classes.flexStart}
              onChange={handleChange}
              placeholder="budgetSelectPlaceholder"
            />
            <div className={classes.iconWrapper}>
              <Icon iconName="icon_help" extension="svg" />
              <div className={clsx(classes.tooltip, classes.budgetTooltip)} dangerouslySetInnerHTML={{ __html: memoT('budgetSelectInfo') }} />
            </div>
          </div>
          <div className={classes.blockWithBtn}>
            <Button
              text="startSearchingButton"
              size="MissionSearchBtn"
            />
          </div>
        </div>
      </form>
    </div>
  )
}

MissionSearch.propTypes = {}

MissionSearch.defaultProps = {}

const mapStateToProps = (state) => ({
  categories: getCategories(state),
})

export default connect(mapStateToProps)(memo(MissionSearch))
